/**
 * Story navigation panel (desktop 1200px+). Dots (stay light blue once revealed), segment lines connect dots (light blue when revealed, dark blue when current segment).
 */

import graphicLockSvg from '../svg/graphic-lock.svg?raw'
import track, { SELECT_CHAPTER } from '../features/analytics'

export interface ChapterItem {
  id: string
  title: string
  sceneIndex: number
}

export interface StoryNavUnits {
  totalSceneUnits: number
  chapterPositions: number[]
}

export interface StoryNavScrollSource {
  getRevealedChapterIndices: () => number[]
  getRevealedSegmentIndices: () => number[]
  /** Chapter whose title scene is covering most of the viewport, or -1. That dot is current. */
  getCurrentChapterIndex: () => number
  /** Segment (non-chapter scene covering most of viewport): segment before that chapter. Line is dark blue when current. */
  getCurrentSegmentIndex: () => number
  /** Get the current scene index in the story data array, or -1 if unknown. */
  getCurrentSceneIndex: () => number
  /** Scroll so the given chapter title is centered in the viewport. */
  scrollToChapter: (chapterIndex: number) => void
}

export class StoryNav {
  private container: HTMLElement
  private chapters: ChapterItem[]
  private scrollSource: StoryNavScrollSource | null = null
  private dots: HTMLElement[] = []
  private segmentLines: HTMLElement[] = []
  private everRevealedDots = new Set<number>()
  private everRevealedSegments = new Set<number>()
  private maxSegmentProgress = new Map<number, number>() // Track maximum progress reached for each segment
  private rafId: number | null = null
  private onOpenChaptersModal: (() => void) | null = null

  constructor(container: HTMLElement, chapters: ChapterItem[], _units: StoryNavUnits) {
    this.container = container
    this.chapters = chapters
    this.buildDOM()
  }

  private buildDOM(): void {
    this.container.innerHTML = ''
    const { chapters } = this

    // Create panel
    const panel = document.createElement('div')
    panel.className = 'story-nav__panel'
    panel.addEventListener('click', () => {
      if (typeof window !== 'undefined' && window.innerWidth < 800) {
        this.onOpenChaptersModal?.()
      }
    })

    // Create arrow element (inside panel, on the right side)
    const arrow = document.createElement('div')
    arrow.className = 'story-nav__arrow'

    const dotsWrap = document.createElement('div')
    dotsWrap.className = 'story-nav__dots'
    const n = chapters.length
    for (let i = 0; i < n; i++) {
      if (i > 0) {
        const line = document.createElement('div')
        line.className = 'story-nav__segment-line'
        line.dataset.segmentIndex = String(i - 1)
        const topPct = n > 1 ? ((i - 1) / (n - 1)) * 100 : 0
        const heightPct = n > 1 ? (1 / (n - 1)) * 100 : 100
        line.style.setProperty('--story-nav-line-top', `${topPct}%`)
        line.style.setProperty('--story-nav-line-height', `${heightPct}%`)
        line.style.setProperty('--story-nav-line-left', `${topPct}%`)
        line.style.setProperty('--story-nav-line-width', `${heightPct}%`)
        
        // Add horizontal indicator (thermometer mark)
        const indicator = document.createElement('div')
        indicator.className = 'story-nav__segment-indicator'
        line.appendChild(indicator)
        
        dotsWrap.appendChild(line)
        this.segmentLines.push(line)
      }
      const ch = chapters[i]
      const dot = document.createElement('div')
      dot.className = 'story-nav__dot'
      dot.setAttribute('aria-label', ch.title)
      dot.setAttribute('role', 'button')
      dot.setAttribute('tabindex', '-1')
      dot.dataset.index = String(i)
      const topPct = n > 1 ? (i / (n - 1)) * 100 : 50
      dot.style.setProperty('--story-nav-dot-top', `${topPct}%`)
      dot.style.setProperty('--story-nav-dot-left', `${topPct}%`)
      const tooltip = document.createElement('span')
      tooltip.className = 'story-nav__tooltip'
      const tooltipText = document.createElement('span')
      tooltipText.className = 'story-nav__tooltip-text'
      tooltipText.textContent = ch.title
      const tooltipLock = document.createElement('span')
      tooltipLock.className = 'story-nav__tooltip-lock'
      tooltipLock.innerHTML = graphicLockSvg
      tooltip.appendChild(tooltipText)
      tooltip.appendChild(tooltipLock)
      dot.addEventListener('click', () => this.onDotClick(i))
      dot.appendChild(tooltip)
      dotsWrap.appendChild(dot)
      this.dots.push(dot)
    }
    panel.appendChild(dotsWrap)
    panel.appendChild(arrow)

    this.container.appendChild(panel)
    this.update([], [], -1, -1)
  }

  private onDotClick(chapterIndex: number): void {
    if (!this.everRevealedDots.has(chapterIndex)) return
    track(SELECT_CHAPTER, this.chapters[chapterIndex]?.id ?? '')
    this.scrollSource?.scrollToChapter(chapterIndex)
  }

  private update(
    revealedChapterIndices: number[],
    revealedSegmentIndices: number[],
    currentSegmentIndex: number,
    currentChapterIndex: number
  ): void {
    if (this.chapters.length === 0) return
    revealedChapterIndices.forEach((i) => this.everRevealedDots.add(i))
    revealedSegmentIndices.forEach((i) => this.everRevealedSegments.add(i))
    if (currentChapterIndex >= 0) this.everRevealedDots.add(currentChapterIndex)
    if (currentSegmentIndex >= 0) this.everRevealedSegments.add(currentSegmentIndex)

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('story-nav__dot--revealed', this.everRevealedDots.has(i))
      dot.classList.toggle('story-nav__dot--current', currentChapterIndex === i)
    })

    // Update segment lines with progress based on scenes between chapters
    const currentSceneIndex = this.scrollSource?.getCurrentSceneIndex() ?? -1
    
    this.segmentLines.forEach((line, i) => {
      line.classList.toggle('story-nav__segment-line--revealed', this.everRevealedSegments.has(i))
      line.classList.toggle('story-nav__segment-line--current', currentSegmentIndex === i)
      
      // Calculate progress based on scenes between chapters
      const n = this.chapters.length
      const baseHeightPct = n > 1 ? (1 / (n - 1)) * 100 : 100
      
      // Find the indicator element
      const indicator = line.querySelector('.story-nav__segment-indicator') as HTMLElement
      
      if (i < this.chapters.length - 1 && currentSceneIndex >= 0) {
        const startChapterIndex = this.chapters[i].sceneIndex
        const endChapterIndex = this.chapters[i + 1].sceneIndex
        const scenesBetween = endChapterIndex - startChapterIndex - 1
        
        if (scenesBetween > 0) {
          let currentProgress = 0
          let isInSegment = false
          
          if (currentSceneIndex <= startChapterIndex) {
            // At or before the start chapter - no progress yet, segment hasn't started
            currentProgress = 0
            isInSegment = false
          } else if (currentSceneIndex >= endChapterIndex) {
            // Past the end chapter - segment is complete
            currentProgress = 100
            isInSegment = false
          } else {
            // Current scene is between the chapters (actually in the segment)
            isInSegment = true
            // Progress = (scenes progressed after start chapter / scenes between) * 100
            // Each scene advances by (100 / scenesBetween)%
            // We're past the start chapter, so scenesProgressed = currentSceneIndex - startChapterIndex
            const scenesProgressed = currentSceneIndex - startChapterIndex
            currentProgress = (scenesProgressed / scenesBetween) * 100
            // Clamp to 0-100
            currentProgress = Math.min(100, Math.max(0, currentProgress))
          }
          
          // Track maximum progress reached for this segment
          // Only update max if we've actually progressed into the segment (past start chapter)
          const currentMax = this.maxSegmentProgress.get(i) ?? 0
          const newMax = currentSceneIndex > startChapterIndex ? Math.max(currentMax, currentProgress) : currentMax
          this.maxSegmentProgress.set(i, newMax)
          
          // Update line height and progress
          // Line should always be full height (gray), progress controls blue portion
          line.style.setProperty('--story-nav-line-height', `${baseHeightPct}%`)
          // Use maximum progress reached (so revealed portions stay blue)
          line.style.setProperty('--story-nav-line-progress', `${newMax}%`)
          
          // Update indicator position and visibility
          if (indicator) {
            line.classList.toggle('story-nav__segment-line--active', isInSegment)
            line.style.setProperty('--story-nav-indicator-position', `${currentProgress}%`)
          }
        } else {
          // No scenes between chapters, line should be full height when revealed
          const progress = this.everRevealedSegments.has(i) ? 100 : 0
          line.style.setProperty('--story-nav-line-height', `${baseHeightPct}%`)
          line.style.setProperty('--story-nav-line-progress', `${progress}%`)
          
          if (indicator) {
            line.classList.remove('story-nav__segment-line--active')
          }
        }
      } else {
        // Fallback: set based on revealed state
        const progress = this.everRevealedSegments.has(i) ? 100 : 0
        line.style.setProperty('--story-nav-line-height', `${baseHeightPct}%`)
        line.style.setProperty('--story-nav-line-progress', `${progress}%`)
        
        if (indicator) {
          line.classList.remove('story-nav__segment-line--active')
        }
      }
    })
  }

  private tick = (): void => {
    const src = this.scrollSource
    if (!src) return
    this.update(
      src.getRevealedChapterIndices(),
      src.getRevealedSegmentIndices(),
      src.getCurrentSegmentIndex(),
      src.getCurrentChapterIndex()
    )
    this.rafId = requestAnimationFrame(this.tick)
  }

  /** Set scroll source and start updating (call when game screen is shown and Lenis is active). */
  start(scrollSource: StoryNavScrollSource): void {
    this.scrollSource = scrollSource
    if (this.rafId === null) this.rafId = requestAnimationFrame(this.tick)
  }

  /** Stop updating (when leaving game screen). */
  stop(): void {
    this.scrollSource = null
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  setVisible(visible: boolean): void {
    this.container.classList.toggle('story-nav--hidden', !visible)
  }

  /** Clear revealed state (e.g. when starting a new game). */
  resetProgress(): void {
    this.everRevealedDots.clear()
    this.everRevealedSegments.clear()
    this.maxSegmentProgress.clear()
  }

  getChapters(): ChapterItem[] {
    return this.chapters
  }

  getRevealedChapterIndices(): number[] {
    return Array.from(this.everRevealedDots)
  }

  scrollToChapter(index: number): void {
    this.scrollSource?.scrollToChapter(index)
  }

  setOnOpenChaptersModal(cb: () => void): void {
    this.onOpenChaptersModal = cb
  }
}
